package com.xorrus.trading.lot.relayclient.browser

import com.xorrus.trading.lot.relay.protocol.sanitizeLabel
import com.xorrus.trading.lot.relayclient.model.DesktopStatus
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.js.Date

/** Anzeigedaten eines gekoppelten Desktops (siehe [DesktopDirectory]). */
@Serializable
data class DesktopInfo(
    /** Vom Nutzer in der connect-SPA vergebener Name dieses Desktops; null = (noch) keiner. */
    val name: String? = null,
    /** Zuletzt gemeldeter Update-Channel (`stable`/`latest`/`dev`). */
    val channel: String? = null,
    val version: String? = null,
    /** Zeitstempel (ms) der letzten erfolgreichen `desktop.status`-Antwort. */
    val lastOnlineAt: Double? = null,
)

/**
 * Anzeigedaten je gekoppeltem Desktop (Schlüssel: Desktop-Kennung = Base64Url der hostId)
 * in `localStorage`:
 *
 * - **Name:** vergibt der Nutzer beim Koppeln (oder später in der Desktop-Liste) in der
 *   connect-SPA. Das ist die EINZIGE Quelle — der Desktop kennt den Namen nicht, und er
 *   gilt nur für diesen Browser. Dadurch funktioniert die Benennung mit jedem Desktop-
 *   Stand, ohne Änderung an der Desktop-App.
 * - **Channel/Version/zuletzt online:** Cache aus `desktop.status`, damit die Liste auch
 *   OFFLINE-Desktops sinnvoll anzeigen kann.
 *
 * Die Wahrheit über Kopplungen bleibt die IndexedDB der Relay-Lib. Dieser Speicher darf
 * jederzeit fehlen (Private Mode, gelöschte Website-Daten) — dann steht ein Platzhalter,
 * und der Nutzer benennt neu.
 */
object DesktopDirectory {

    /** Obergrenze für Desktop-Namen — passt in eine Listenzeile auf dem Telefon. */
    const val MAX_NAME_LENGTH = 40

    private const val STORAGE_KEY = "lot.desktops"
    private val json = Json { ignoreUnknownKeys = true }

    private var cache: MutableMap<String, DesktopInfo>? = null

    private fun load(): MutableMap<String, DesktopInfo> {
        cache?.let { return it }
        val raw = runCatching { window.localStorage.getItem(STORAGE_KEY) }.getOrNull()
        val parsed = raw?.let { runCatching { json.decodeFromString<Map<String, DesktopInfo>>(it) }.getOrNull() }
        return (parsed ?: emptyMap()).toMutableMap().also { cache = it }
    }

    private fun save(entries: Map<String, DesktopInfo>) {
        runCatching { window.localStorage.setItem(STORAGE_KEY, json.encodeToString(entries)) }
    }

    fun get(id: String): DesktopInfo = load()[id] ?: DesktopInfo()

    /** Anzeigename: der vergebene Name, sonst ein Platzhalter aus der Desktop-Kennung. */
    fun displayName(id: String): String =
        get(id).name?.takeIf { it.isNotBlank() } ?: "Desktop ${id.take(6)}"

    /** Name setzen oder (bei leerer Eingabe) entfernen; bereinigt und auf [MAX_NAME_LENGTH] gekürzt. */
    fun rename(id: String, name: String) {
        val entries = load()
        val clean = sanitizeLabel(name).trim().take(MAX_NAME_LENGTH).trim()
        entries[id] = (entries[id] ?: DesktopInfo()).copy(name = clean.ifBlank { null })
        save(entries)
    }

    /** Channel/Version aus einer `desktop.status`-Antwort nachführen; der Name bleibt unberührt. */
    fun remember(id: String, status: DesktopStatus) {
        val entries = load()
        val old = entries[id] ?: DesktopInfo()
        entries[id] = old.copy(
            channel = status.channel ?: old.channel,
            version = status.version ?: old.version,
            lastOnlineAt = Date.now(),
        )
        save(entries)
    }

    fun forget(id: String) {
        val entries = load()
        if (entries.remove(id) != null) save(entries)
    }

    /** Anzeigedaten eines ersetzten Eintrags (z. B. rotierte hostId desselben Desktops) übernehmen. */
    fun move(from: String, to: String) {
        val entries = load()
        val info = entries.remove(from) ?: return
        if (entries[to]?.name == null) entries[to] = info
        save(entries)
    }
}
